import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  formatEatTime,
  formatMoney,
  isAbnormalLabInterpretation,
  labFlag,
  moneyFromCents,
} from '@wodi/domain';
import {
  LAB_GATE_LABELS,
  type ImagingReportView,
  type LabOrderView,
  type ReleasedLabReportView,
  type ServiceOrderView,
  type WorkspaceView,
} from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  Kbd,
  LoadingBlock,
  Notice,
  RadioGroup,
  SearchPicker,
  Section,
  Tag,
  TextArea,
  TextField,
  type SearchPickerStatus,
  type TagState,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { visitPreauthGatesQuery } from '../api/claims.ts';
import { ApiError } from '../api/client.ts';
import {
  cancelLabOrder,
  cancelServiceOrder,
  performServiceOrder,
  placeLabOrders,
  placeServiceOrders,
  searchLabTests,
  searchServiceItems,
  visitOrdersQuery,
  type ServiceOrderKind,
} from '../api/consultation.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import styles from './consultation.module.css';
import { ShaApproval } from './sha-approval.tsx';
import { PRELIMINARY } from '../imaging/format.ts';
import { ImagingReportBody, ReportStatusTag } from '../imaging/report-view.tsx';

// Orders and results (plan §7.2): lab tests through the lab's own contract, procedures and
// imaging requests through the consultation's, one list with each order's state in the
// lab's words, and released results arriving by polling, abnormal first.

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

type Price = { state: 'priced'; unitPriceCents: string } | { state: 'not_priced'; message: string };

function priceWords(price: Price): string {
  return price.state === 'priced'
    ? formatMoney(moneyFromCents(BigInt(price.unitPriceCents)))
    : 'Not priced';
}

interface Pickable {
  serviceItemId: string;
  code: string;
  name: string;
  price: Price;
  detail?: string;
}

function ItemPicker({
  label,
  search,
  onSelect,
  queryKey,
}: {
  label: string;
  search: (query: string) => Promise<{ items: Pickable[] }>;
  onSelect: (item: Pickable) => void;
  queryKey: string;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const results = useQuery({
    queryKey: ['order-search', queryKey, trimmed],
    queryFn: () => search(trimmed),
    enabled: online && trimmed.length >= 2,
    staleTime: 30_000,
    retry: false,
  });
  let status: SearchPickerStatus;
  if (trimmed.length < 2) status = 'idle';
  else if (!online) status = 'offline';
  else if (results.isFetching || results.isPending) status = 'searching';
  else if (results.isError) status = 'error';
  else status = 'ready';
  return (
    <SearchPicker
      label={label}
      inputValue={text}
      onInputChange={setText}
      status={status}
      items={results.data?.items ?? []}
      itemsQuery={trimmed}
      getKey={(i) => i.serviceItemId}
      getTextValue={(i) => `${i.code} ${i.name}`}
      renderItem={(i) => (
        <span className={styles.itemMain}>
          <span>
            <span className={`wodi-id ${styles.code}`}>{i.code}</span>
            {i.name}
          </span>
          <span className={styles.itemMeta}>
            {[i.detail, priceWords(i.price)].filter((x) => x !== undefined).join(' · ')}
          </span>
        </span>
      )}
      onSelect={(item) => {
        onSelect(item);
        setText('');
      }}
      isExactMatch={(i, q) => i.code.toLowerCase() === q.trim().toLowerCase()}
      emptyMessage="Nothing matches. Check the spelling or search by code."
      errorMessage={errorText(results.error, 'Search did not work. Try again.')}
      offlineMessage="Searching needs a connection to Wodi."
      autoFocus
    />
  );
}

function OrderTests({ view, onDone }: { view: WorkspaceView; onDone: () => void }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<Pickable[]>([]);
  const [priority, setPriority] = useState<'routine' | 'urgent'>('routine');
  const [notes, setNotes] = useState('');
  const [requestId] = useState(() => crypto.randomUUID());
  const place = useMutation({
    mutationFn: () =>
      placeLabOrders(view.visit.id, {
        clientRequestId: requestId,
        orderingUserId: session?.user.id ?? '',
        tests: chosen.map((c) => ({
          serviceItemId: c.serviceItemId,
          priority,
          clinicalNotes: notes.trim() === '' ? null : notes.trim(),
        })),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'orders'] });
      toasts.show({ title: 'Tests requested' });
      for (const w of result.warnings) toasts.show({ title: w.message });
      onDone();
    },
  });
  return (
    <div className={styles.form}>
      <ItemPicker
        label="Test"
        queryKey={`lab:${view.visit.id}`}
        search={async (q) => {
          const result = await searchLabTests(view.visit.id, q);
          return {
            items: result.items.map((i) => ({
              ...i,
              detail: `${i.sectionName}, ${i.specimenType}`,
            })),
          };
        }}
        onSelect={(item) => {
          setChosen((prev) =>
            prev.some((p) => p.serviceItemId === item.serviceItemId) ? prev : [...prev, item],
          );
        }}
      />
      {chosen.length === 0 ? null : (
        <ul className={styles.list} aria-label="Tests to request">
          {chosen.map((c) => (
            <li key={c.serviceItemId} className={styles.item}>
              <span>{`${c.name} · ${priceWords(c.price)}`}</span>
              <Button
                dense
                variant="quiet"
                onPress={() => {
                  setChosen((prev) => prev.filter((p) => p.serviceItemId !== c.serviceItemId));
                }}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <RadioGroup
        label="Priority"
        orientation="horizontal"
        value={priority}
        onChange={(v) => {
          setPriority(v as 'routine' | 'urgent');
        }}
        options={[
          { id: 'routine', label: 'Routine' },
          { id: 'urgent', label: 'Urgent' },
        ]}
      />
      <TextField label="Clinical note for the lab (optional)" value={notes} onChange={setNotes} />
      {place.error === null ? null : (
        <Notice tone="error">{errorText(place.error, 'The tests could not be requested.')}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={chosen.length === 0}
          isPending={place.isPending}
          pendingLabel="Sending…"
          onPress={() => {
            place.mutate();
          }}
        >
          Send to lab
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const KIND_WORDS: Record<ServiceOrderKind, { label: string; requested: string; done: string }> = {
  procedure: { label: 'Procedure', requested: 'Procedure requested', done: 'Procedure recorded' },
  imaging: { label: 'Imaging', requested: 'Imaging requested', done: 'Imaging recorded' },
};

function OrderService({
  view,
  kind,
  consultationId,
  mayPerform,
  onDone,
}: {
  view: WorkspaceView;
  kind: ServiceOrderKind;
  consultationId: string | null;
  mayPerform: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [item, setItem] = useState<Pickable | null>(null);
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState('');
  const [requestId] = useState(() => crypto.randomUUID());
  const place = useMutation({
    mutationFn: (now: boolean) =>
      placeServiceOrders(kind, view.visit.id, {
        clientRequestId: requestId,
        consultationId,
        orders: [
          {
            serviceItemId: item?.serviceItemId ?? '',
            reason: reason.trim() === '' ? null : reason.trim(),
            performNow: now ? { outcomeText: outcome.trim() } : null,
          },
        ],
      }),
    onSuccess: async (result, now) => {
      await queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'orders'] });
      toasts.show({ title: now ? KIND_WORDS[kind].done : KIND_WORDS[kind].requested });
      for (const w of result.warnings) toasts.show({ title: w.message });
      onDone();
    },
  });
  return (
    <div className={styles.form}>
      {item === null ? (
        <ItemPicker
          label={KIND_WORDS[kind].label}
          queryKey={`${kind}:${view.visit.id}`}
          search={(q) => searchServiceItems(kind, view.visit.id, q)}
          onSelect={setItem}
        />
      ) : (
        <p className={styles.itemTitle}>
          <span className={`wodi-id ${styles.code}`}>{item.code}</span>
          {`${item.name} · ${priceWords(item.price)} `}
          <Button
            dense
            variant="quiet"
            onPress={() => {
              setItem(null);
            }}
          >
            Change
          </Button>
        </p>
      )}
      <TextField
        label="Reason or clinical question (optional)"
        value={reason}
        onChange={setReason}
      />
      {mayPerform ? (
        <TextArea
          label="What was done (for Do it now)"
          value={outcome}
          onChange={setOutcome}
          rows={2}
        />
      ) : null}
      {place.error === null ? null : (
        <Notice tone="error">{errorText(place.error, 'The request could not be placed.')}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={item === null}
          isPending={place.isPending && !place.variables}
          pendingLabel="Requesting…"
          onPress={() => {
            place.mutate(false);
          }}
        >
          Request
        </Button>
        {mayPerform ? (
          <Button
            isDisabled={item === null || outcome.trim() === ''}
            isPending={place.isPending && place.variables}
            pendingLabel="Recording…"
            onPress={() => {
              place.mutate(true);
            }}
          >
            Do it now
          </Button>
        ) : null}
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function labState(order: LabOrderView): string {
  if (order.status === 'cancelled') return 'Cancelled';
  if (order.status === 'entered_in_error') return 'Entered in error';
  if (order.report?.status === 'final') return 'Result ready';
  if (order.status === 'completed') return 'Completed';
  if (order.gate !== 'ready') return LAB_GATE_LABELS[order.gate];
  if (order.report !== null) return 'In the lab';
  if (order.sample !== null) return 'Collected';
  return LAB_GATE_LABELS.ready;
}

/** The tag's state: held by payment or approval needs attention; stopped orders are neutral. */
function orderTag(order: LabOrderView | ServiceOrderView, finished: boolean): TagState {
  if (order.status === 'cancelled' || order.status === 'entered_in_error') return 'neutral';
  if (finished) return 'done';
  return order.gate === 'ready' ? 'pending' : 'attention';
}

function serviceState(order: ServiceOrderView): string {
  if (order.status === 'performed') return 'Done';
  if (order.status === 'cancelled') return 'Cancelled';
  if (order.status === 'entered_in_error') return 'Entered in error';
  return order.gate === 'ready' ? 'Requested' : LAB_GATE_LABELS[order.gate];
}

function ResultTable({ report }: { report: ReleasedLabReportView }) {
  const rows = report.results
    .map((r, index) => ({ r, index }))
    .sort(
      (a, b) =>
        Number(isAbnormalLabInterpretation(b.r.interpretation)) -
          Number(isAbnormalLabInterpretation(a.r.interpretation)) || a.index - b.index,
    );
  return (
    <table className={styles.results}>
      <caption className={styles.help}>
        {`${report.testName}, released ${report.released === null ? '' : formatEatTime(new Date(report.released.at))}`}
      </caption>
      <thead>
        <tr>
          <th scope="col">Test</th>
          <th scope="col">Result</th>
          <th scope="col">Flag</th>
          <th scope="col">Range</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ r }) => {
          const flag = labFlag(r.interpretation);
          return (
            <tr key={r.id}>
              <th scope="row">{r.analyteName}</th>
              <td
                data-abnormal={flag.emphasised || undefined}
                data-critical={flag.critical || undefined}
              >
                {`${r.display}${r.unit === null ? '' : ` ${r.unit.display}`}`}
              </td>
              <td
                data-abnormal={flag.emphasised || undefined}
                data-critical={flag.critical || undefined}
              >
                {flag.marker === '' ? flag.word : `${flag.marker} ${flag.word}`}
              </td>
              <td>
                {r.range === null
                  ? ''
                  : `${r.range.text}${r.range.provisional ? ' (provisional)' : ''}`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function OrdersPanel({
  view,
  consultationId,
}: {
  view: WorkspaceView;
  consultationId: string | null;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const orders = useQuery(visitOrdersQuery(view.visit.id));
  const mayReadApprovals = session !== null && can(session, 'preauth.read');
  const approvals = useQuery({
    ...visitPreauthGatesQuery(view.visit.id),
    enabled: mayReadApprovals,
  });
  const [mode, setMode] = useState<'none' | 'tests' | ServiceOrderKind>('none');
  const [cancelling, setCancelling] = useState<
    { kind: 'lab'; id: string } | { kind: ServiceOrderKind; id: string } | null
  >(null);
  const [performing, setPerforming] = useState<ServiceOrderView | null>(null);
  const [viewingReport, setViewingReport] = useState<ImagingReportView | null>(null);
  const [outcome, setOutcome] = useState('');
  const open = view.visit.status === 'open' && online;
  const may = (p: Parameters<typeof can>[1]) => session !== null && can(session, p);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'orders'] });

  const cancel = useMutation({
    mutationFn: async (reason: string) => {
      if (cancelling === null) throw new Error('nothing to cancel');
      if (cancelling.kind === 'lab') {
        await cancelLabOrder(cancelling.id, reason);
        return null;
      }
      return (await cancelServiceOrder(cancelling.kind, cancelling.id, reason)).charge;
    },
    onSuccess: async (charge) => {
      setCancelling(null);
      await refresh();
      toasts.show({ title: 'Request cancelled' });
      if (charge === 'needs_credit_note')
        toasts.show({ title: 'Paid: a credit note request is needed at the cashier' });
    },
  });
  const perform = useMutation({
    mutationFn: () =>
      performServiceOrder(
        performing?.kind ?? 'procedure',
        performing?.id ?? '',
        outcome.trim() === '' ? null : outcome.trim(),
      ),
    onSuccess: async () => {
      setPerforming(null);
      setOutcome('');
      await refresh();
      toasts.show({ title: 'Recorded as done' });
    },
  });

  useShortcut(
    'alt+l',
    () => {
      if (open && may('lab.order')) setMode('tests');
    },
    { description: 'Order tests' },
  );
  useShortcut(
    'alt+p',
    () => {
      if (open && may('procedures.order')) setMode('procedure');
    },
    { description: 'Order procedure' },
  );

  const data = orders.data;
  const approval = (sourceId: string, serviceName: string) => {
    const item = approvals.data?.items.find((i) => i.source.id === sourceId);
    if (item === undefined) return null;
    return (
      <ShaApproval
        item={item}
        serviceName={serviceName}
        visitId={view.visit.id}
        diagnoses={view.diagnoses}
        mayRequest={may('preauth.request')}
        mayOverride={may('preauth.override')}
        canAct={open}
      />
    );
  };
  const hasOrders = data !== undefined && data.lab.length + data.services.length > 0;
  return (
    <Section title="Orders and results">
      {view.visit.status === 'open' ? (
        mode === 'none' ? (
          <div className={styles.actions}>
            {may('lab.order') ? (
              <>
                <Button
                  shortcut="alt+l"
                  isDisabled={!online}
                  onPress={() => {
                    setMode('tests');
                  }}
                >
                  Order tests
                </Button>
                <Kbd combo="alt+l" />
              </>
            ) : null}
            {may('procedures.order') ? (
              <>
                <Button
                  shortcut="alt+p"
                  isDisabled={!online}
                  onPress={() => {
                    setMode('procedure');
                  }}
                >
                  Order procedure
                </Button>
                <Kbd combo="alt+p" />
              </>
            ) : null}
            {may('imaging.order') ? (
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setMode('imaging');
                }}
              >
                Request imaging
              </Button>
            ) : null}
          </div>
        ) : mode === 'tests' ? (
          <OrderTests
            view={view}
            onDone={() => {
              setMode('none');
            }}
          />
        ) : (
          <OrderService
            key={mode}
            view={view}
            kind={mode}
            consultationId={consultationId}
            mayPerform={may(mode === 'procedure' ? 'procedures.perform' : 'imaging.perform')}
            onDone={() => {
              setMode('none');
            }}
          />
        )
      ) : null}
      {!online ? <Notice tone="info">Ordering needs a connection to Wodi.</Notice> : null}
      {orders.isPending ? <LoadingBlock label="Loading orders" rows={3} /> : null}
      {orders.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void orders.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(orders.error, 'The orders could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {hasOrders && mayReadApprovals && approvals.isPending ? (
        <p className={styles.help}>Checking which orders need SHA approval…</p>
      ) : null}
      {hasOrders && approvals.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void approvals.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(
            approvals.error,
            'Which orders need SHA approval could not be loaded. Try again before doing them.',
          )}
        </Notice>
      ) : null}
      {data?.lab.length === 0 && data.services.length === 0 ? (
        <p className={styles.help}>No tests or procedures ordered on this visit.</p>
      ) : null}
      {data === undefined ? null : (
        <ul className={styles.list} aria-label="Orders on this visit">
          {data.lab.map((o) => (
            <li
              key={o.id}
              className={styles.item}
              data-inactive={o.status === 'cancelled' || undefined}
            >
              <span className={styles.itemMain}>
                <span className={styles.itemTitle}>{o.testName}</span>
                <span className={styles.itemMeta}>
                  {`Lab · ${formatEatTime(new Date(o.orderedAt))}${o.priority === 'urgent' ? ' · Urgent' : ''}`}
                </span>
                {approval(o.id, o.testName)}
              </span>
              <span className={styles.actions}>
                <Tag state={orderTag(o, o.report?.status === 'final' || o.status === 'completed')}>
                  {labState(o)}
                </Tag>
                {o.status === 'active' && o.sample === null && may('lab.order.cancel') && open ? (
                  <Button
                    dense
                    variant="quiet"
                    onPress={() => {
                      setCancelling({ kind: 'lab', id: o.id });
                    }}
                  >
                    Cancel
                  </Button>
                ) : null}
              </span>
            </li>
          ))}
          {data.services.map((o) => {
            // A signed imaging report (radiology plan §6): its state and impression here.
            const imaging = data.imagingReports.find((r) => r.orderId === o.id) ?? null;
            return (
              <li
                key={o.id}
                className={styles.item}
                data-inactive={o.status === 'cancelled' || undefined}
              >
                <span className={styles.itemMain}>
                  <span className={styles.itemTitle}>{o.itemName}</span>
                  <span className={styles.itemMeta}>
                    {[
                      `${KIND_WORDS[o.kind].label} · ${formatEatTime(new Date(o.orderedAt))}`,
                      o.outcomeText,
                      o.amendments.length === 0 ? null : 'Amended',
                    ]
                      .filter((x) => x !== null)
                      .join(' · ')}
                  </span>
                  {imaging === null ? null : (
                    <span
                      className={styles.itemMeta}
                    >{`Impression: ${imaging.current.impression}`}</span>
                  )}
                  {approval(o.id, o.itemName)}
                </span>
                <span className={styles.actions}>
                  {imaging === null ? (
                    <Tag state={orderTag(o, o.status === 'performed')}>{serviceState(o)}</Tag>
                  ) : (
                    <>
                      {imaging.enteredInError !== null ? (
                        <Tag state="neutral">Entered in error</Tag>
                      ) : (
                        <Tag state={imaging.status === 'verified' ? 'done' : 'attention'}>
                          {imaging.status === 'verified' ? 'Report verified' : PRELIMINARY}
                        </Tag>
                      )}
                      <Button
                        dense
                        variant="quiet"
                        aria-label={`View the ${o.itemName} report`}
                        onPress={() => {
                          setViewingReport(imaging);
                        }}
                      >
                        View report
                      </Button>
                    </>
                  )}
                  {o.status === 'requested' && open ? (
                    <>
                      {may(o.kind === 'procedure' ? 'procedures.perform' : 'imaging.perform') ? (
                        <Button
                          dense
                          variant="quiet"
                          isDisabled={o.gate !== 'ready'}
                          onPress={() => {
                            setPerforming(o);
                          }}
                        >
                          Record as done
                        </Button>
                      ) : null}
                      {may(o.kind === 'procedure' ? 'procedures.cancel' : 'imaging.order') ? (
                        <Button
                          dense
                          variant="quiet"
                          onPress={() => {
                            setCancelling({ kind: o.kind, id: o.id });
                          }}
                        >
                          Cancel
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {data === undefined || data.results.length === 0 ? null : (
        <div className={styles.panel}>
          <h3 className={styles.subheading}>Results</h3>
          {data.results.map((r) => (
            <ResultTable key={r.id} report={r} />
          ))}
        </div>
      )}
      {data?.resultsHidden === true && data.lab.length > 0 ? (
        <p className={styles.help}>Results are shown to staff who may read lab results.</p>
      ) : null}
      <ReasonDialog
        title="Cancel the request"
        isOpen={cancelling !== null}
        onOpenChange={(o) => {
          if (!o) setCancelling(null);
        }}
        confirmLabel="Cancel request"
        pendingLabel="Cancelling…"
        isPending={cancel.isPending}
        error={cancel.error === null ? null : errorText(cancel.error, 'It could not be cancelled.')}
        onConfirm={(reason) => {
          cancel.mutate(reason);
        }}
      >
        <p>An unpaid charge for it is cancelled too. A paid one needs a credit note.</p>
      </ReasonDialog>
      {viewingReport === null ? null : (
        <Dialog
          title={`${viewingReport.itemName} report`}
          isOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) setViewingReport(null);
          }}
        >
          <p className={styles.itemMeta}>
            {`${view.visit.visitNumber}, `}
            <ReportStatusTag report={viewingReport} />
          </p>
          <ImagingReportBody report={viewingReport} />
          <DialogActions>
            <Button
              variant="primary"
              onPress={() => {
                setViewingReport(null);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {data?.imagingReportsHidden === true &&
      data.services.some((o) => o.kind === 'imaging' && o.status === 'performed') ? (
        <p className={styles.help}>Imaging reports are shown to staff who may read them.</p>
      ) : null}
      <RecordDoneForm
        order={performing}
        outcome={outcome}
        onOutcome={setOutcome}
        isPending={perform.isPending}
        error={
          perform.error === null ? null : errorText(perform.error, 'It could not be recorded.')
        }
        onClose={() => {
          setPerforming(null);
        }}
        onConfirm={() => {
          perform.mutate();
        }}
      />
    </Section>
  );
}

/** "Record as done": the outcome in words; no reason is needed to record work. */
function RecordDoneForm({
  order,
  outcome,
  onOutcome,
  isPending,
  error,
  onClose,
  onConfirm,
}: {
  order: ServiceOrderView | null;
  outcome: string;
  onOutcome: (v: string) => void;
  isPending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (order === null) return null;
  return (
    <div className={styles.form} role="group" aria-label={`Record ${order.itemName} as done`}>
      <p className={styles.itemTitle}>{`Record ${order.itemName} as done`}</p>
      <TextArea label="What was done" value={outcome} onChange={onOutcome} rows={3} />
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isPending={isPending}
          pendingLabel="Recording…"
          onPress={onConfirm}
        >
          Record as done
        </Button>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
